import React, { useState, useEffect, useRef } from 'react'
import GoldSiverHeader from '../HomePage/GoldSiverHeader'
import { Card, Col, Row, Button } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/axios';
import { API_ENDPOINTS } from '../../api/endpoint';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import '../../assets/css/pokemonMarket.css';
import LearnAttackModal from '../Component/LearnAttackModal';
import { useDispatch } from 'react-redux';
import { getPlayerPokemons } from '../../store/pokemon';

function Map() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [hoveredArea, setHoveredArea] = useState(null);
    const [maps, setMaps] = useState([]);
    const [currentMapIndex, setCurrentMapIndex] = useState(0);
    const [mapPoints, setMapPoints] = useState([]);
    const [loading, setLoading] = useState(true);
    const mapContainerRef = useRef(null);
    const [mapImageSize, setMapImageSize] = useState({ width: 0, height: 0 });
    const [originalImageSize, setOriginalImageSize] = useState({ width: 0, height: 0 });
    const navigate = useNavigate();
    
    // State for learn new attack popup - check localStorage on mount
    const [showLearnAttackModal, setShowLearnAttackModal] = useState(false);
    const [pendingAttackData, setPendingAttackData] = useState(null);
    
    // Check for pending attack data when component mounts (after returning from battle)
    useEffect(() => {
        const saved = localStorage.getItem('pendingAttackData');
        if (saved) {
            console.log('[MAP] Found pending attack data in localStorage:', saved);
            try {
                const data = JSON.parse(saved);
                setPendingAttackData(data);
                setShowLearnAttackModal(true);
            } catch (e) {
                console.error('[MAP] Error parsing pending attack data:', e);
                localStorage.removeItem('pendingAttackData');
            }
        }
    }, []);
    
    const loadMaps = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get(API_ENDPOINTS.GET_MAPS);
            if (response.data && response.data.success) {
                const loadedMaps = response.data.maps || [];
                setMaps(loadedMaps);
                if (loadedMaps.length > 0) {
                    setCurrentMapIndex(0);
                }
            }
        } catch (error) {
            console.error('Error loading maps:', error);
            toast.error('Failed to load maps');
        } finally {
            setLoading(false);
        }
    };
    
    const loadMapPoints = async (mapId) => {
        if (!mapId) return;
        try {
            const response = await axiosInstance.get(`${API_ENDPOINTS.GET_MAP_POINTS}?map_id=${mapId}`);
            if (response.data && response.data.success) {
                setMapPoints(response.data.points || []);
            } else {
                setMapPoints([]);
            }
        } catch (error) {
            console.error('Error loading map points:', error);
            toast.error('Failed to load map points');
            setMapPoints([]);
        }
    };
    
    useEffect(() => {
        loadMaps();
    }, []);
    
    useEffect(() => {
        if (maps.length > 0 && maps[currentMapIndex] && maps[currentMapIndex].id) {
            loadMapPoints(maps[currentMapIndex].id);
        }
    }, [maps, currentMapIndex]);
    
    // Calculate current map
    const currentMap = maps.length > 0 ? maps[currentMapIndex] : null;
    
    // Calculate map image size when it loads
    useEffect(() => {
        if (!currentMap) {
            setMapImageSize({ width: 0, height: 0 });
            return;
        }
        
        const handleResize = () => {
            if (mapContainerRef.current && currentMap) {
                const container = mapContainerRef.current;
                const containerRect = container.getBoundingClientRect();
                
                // Calculate actual displayed image size with background-size: contain
                if (originalImageSize.width > 0 && originalImageSize.height > 0) {
                    const containerAspect = containerRect.width / containerRect.height;
                    const imageAspect = originalImageSize.width / originalImageSize.height;
                    
                    let displayedWidth, displayedHeight;
                    if (imageAspect > containerAspect) {
                        // Image is wider - fit to width
                        displayedWidth = containerRect.width;
                        displayedHeight = containerRect.width / imageAspect;
                    } else {
                        // Image is taller - fit to height
                        displayedHeight = containerRect.height;
                        displayedWidth = containerRect.height * imageAspect;
                    }
                    
                    setMapImageSize({
                        width: displayedWidth,
                        height: displayedHeight
                    });
                }
            }
        };
        
        window.addEventListener('resize', handleResize);
        // Small delay to ensure image is rendered
        const timeoutId = setTimeout(handleResize, 100);
        
        return () => {
            window.removeEventListener('resize', handleResize);
            clearTimeout(timeoutId);
        };
    }, [currentMap?.id, mapPoints.length, originalImageSize.width, originalImageSize.height]);
    
    const handleMouseEnter = (area) => {
        setHoveredArea(area);
    };

    const handleMouseLeave = () => {
        setHoveredArea(null);
    };
    
    const handlePointClick = async (point) => {
        // Check if point is unlocked
        if (point.is_unlocked === false) {
            const requirement = point.unlock_requirement;
            if (requirement) {
                toast.warning(
                    `נקודה זו נעולה. עליך לתקוף את "${requirement.required_on_point_name}" ${requirement.required_attacks} פעמים. (${requirement.current_attacks}/${requirement.required_attacks})`
                );
            } else {
                toast.warning('נקודה זו נעולה. עליך להשלים דרישות קודמות.');
            }
            return;
        }
        
        if (point.point_type === 'navigation' && point.target_url) {
            // Navigation point - navigate to the target URL
            navigate(point.target_url);
        } else if (point.point_type === 'trainer' && point.trainer_id) {
            // Trainer battle point - start trainer battle
            try {
                const response = await axiosInstance.post(API_ENDPOINTS.START_TRAINER_BATTLE, {
                    map_point_id: point.id
                });
                
                if (response.data && response.data.success) {
                    // Navigate to battle page with trainer battle parameters
                    navigate(`/battle?trainer=true&battle_id=${response.data.battle_id}&map_point_id=${point.id}`);
                } else {
                    toast.error(response.data?.error || 'Failed to start trainer battle');
                }
            } catch (error) {
                console.error('Error starting trainer battle:', error);
                const errorMessage = error.response?.data?.error || error.response?.data?.message || error.message || 'Failed to start trainer battle';
                
                // Check if it's an authorization error
                if (error.response?.status === 401 || errorMessage.includes('Authorization') || errorMessage.includes('token')) {
                    toast.error('Session expired. Please log in again.');
                    // Optionally redirect to login
                    setTimeout(() => {
                        navigate('/login');
                    }, 2000);
                } else {
                    toast.error(errorMessage);
                }
            }
        } else {
            // Battle point - navigate to battle
            navigate(`/battle?map=true&gebied=${point.gebied}&point_id=${point.id}`);
        }
    };
    
    const handlePreviousMap = () => {
        if (currentMapIndex > 0) {
            setCurrentMapIndex(currentMapIndex - 1);
        }
    };
    
    const handleNextMap = () => {
        if (currentMapIndex < maps.length - 1) {
            setCurrentMapIndex(currentMapIndex + 1);
        }
    };
    
    const handleImageLoad = (e) => {
        if (mapContainerRef.current) {
            const img = e.target;
            
            // Store original image dimensions (natural size)
            if (img.naturalWidth && img.naturalHeight) {
                setOriginalImageSize({
                    width: img.naturalWidth,
                    height: img.naturalHeight
                });
                
                // Calculate displayed size with background-size: contain
                const container = mapContainerRef.current;
                const containerRect = container.getBoundingClientRect();
                const containerAspect = containerRect.width / containerRect.height;
                const imageAspect = img.naturalWidth / img.naturalHeight;
                
                let displayedWidth, displayedHeight;
                if (imageAspect > containerAspect) {
                    // Image is wider - fit to width
                    displayedWidth = containerRect.width;
                    displayedHeight = containerRect.width / imageAspect;
                } else {
                    // Image is taller - fit to height
                    displayedHeight = containerRect.height;
                    displayedWidth = containerRect.height * imageAspect;
                }
                
                setMapImageSize({
                    width: displayedWidth,
                    height: displayedHeight
                });
            }
        }
    };
    
    // Calculate point position - exactly like in Admin
    // The point should stay in the exact same position regardless of screen size
    const getPointStyle = (point) => {
        if (!mapContainerRef.current || !mapImageSize.width || !mapImageSize.height || !originalImageSize.width || !originalImageSize.height) {
            return { display: 'none' };
        }
        
        // Calculate scale factor based on displayed image size vs original
        const scale = mapImageSize.width / originalImageSize.width;
        
        // Scale width and height proportionally
        const scaledWidth = point.width * scale;
        const scaledHeight = point.height * scale;
        
        // Calculate offset for centered image (background-size: contain)
        const containerRect = mapContainerRef.current.getBoundingClientRect();
        const containerAspect = containerRect.width / containerRect.height;
        const imageAspect = originalImageSize.width / originalImageSize.height;
        
        let offsetX = 0, offsetY = 0;
        if (imageAspect > containerAspect) {
            // Image is wider - centered vertically
            offsetY = (containerRect.height - mapImageSize.height) / 2;
        } else {
            // Image is taller - centered horizontally
            offsetX = (containerRect.width - mapImageSize.width) / 2;
        }
        
        // Calculate position in pixels based on percentage of displayed image
        const left = (point.x / 100) * mapImageSize.width + offsetX;
        const top = (point.y / 100) * mapImageSize.height + offsetY;
        
        return {
            position: 'absolute',
            left: `${left}px`,
            top: `${top}px`,
            width: `${scaledWidth}px`,
            height: `${scaledHeight}px`,
            // No transform - position from top-left corner like in Admin
            border: hoveredArea?.id === point.id ? '1px solid red' : '1px solid blue',
            backgroundColor: hoveredArea?.id === point.id ? 'rgba(255, 0, 0, 0.3)' : 'rgba(0, 0, 255, 0.2)',
            cursor: 'pointer',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease',
            boxShadow: hoveredArea?.id === point.id ? '0 0 10px rgba(255, 0, 0, 0.5)' : '0 0 5px rgba(0, 0, 255, 0.3)',
            zIndex: hoveredArea?.id === point.id ? 10 : 5
        };
    };
    
    return (
        <div>
            <GoldSiverHeader previous={'/home'} title='Map'>
                {/* Header Section - Similar to Backpack */}
                <section className="ar_market_filter_section">
                    <div className="container">
                        <div className="row">
                            <div className="col-md-12">
                                <div className="ar_market_filter_title">
                                    <div className="ar_market_cover_image_wrapper">
                                        <img 
                                            src="/images/cover/1.png" 
                                            alt="Map Cover" 
                                            className="ar_market_cover_image" 
                                            onError={(e) => {
                                                e.target.src = "/images/cover/1.png";
                                            }}
                                        />
                                    </div>
                                    <div className="ar_market_cover_overlay"></div>
                                    <div className="ar_market_cover_content">
                                        <h1>{currentMap ? currentMap.name : t('Map')}</h1>
                                        <p>
                                            {currentMap?.city_name || t('Explore different areas and battle wild Pokémon')}
                                            {currentMap?.required_level > 0 && (
                                                <span className="ms-2">({t('Level')} {currentMap.required_level}+)</span>
                                            )}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
                <div className="container">
                <div>
                    {loading ? (
                        <Card border='dark' text='white' className='bg-theme mt-2'>
                            <Card.Body className='text-center'>
                                <p>{t('Loading maps...')}</p>
                            </Card.Body>
                        </Card>
                    ) : maps.length === 0 ? (
                        <Card border='dark' text='white' className='bg-theme mt-2'>
                        <Card.Body className='text-center'>
                                <p>{t('No maps available. Please contact an administrator.')}</p>
                        </Card.Body>
                    </Card>
                    ) : (
                        <>
                            {/* Map Slider Controls */}
                            {maps.length > 1 && (
                    <Card border='dark' text='white' className='bg-theme mt-2'>
                                    <Card.Body className='d-flex justify-content-between align-items-center'>
                                        <Button 
                                            variant="primary" 
                                            onClick={handlePreviousMap}
                                            disabled={currentMapIndex === 0}
                                        >
                                            ← {t('Previous')}
                                        </Button>
                                        <span>
                                            {t('Map')} {currentMapIndex + 1} {t('of')} {maps.length}
                                        </span>
                                        <Button 
                                            variant="primary" 
                                            onClick={handleNextMap}
                                            disabled={currentMapIndex === maps.length - 1}
                                        >
                                            {t('Next')} →
                                        </Button>
                                    </Card.Body>
                                </Card>
                            )}
                            
                            {/* Map Display */}
                            {currentMap && (
                                <div className="ar_market_table_area mt-3">
                                    <div 
                                        ref={mapContainerRef}
                                        style={{
                                            position: 'relative',
                                            width: '100%',
                                            maxWidth: '1200px',
                                            margin: '0 auto',
                                            minHeight: '700px',
                                            maxHeight: '800px',
                                            backgroundImage: `url(${currentMap.image_path})`,
                                            backgroundSize: 'contain',
                                            backgroundRepeat: 'no-repeat',
                                            backgroundPosition: 'center',
                                            backgroundAttachment: 'local'
                                        }}
                                    >
                                        {/* Hidden img to get natural dimensions */}
                                        <img
                                            src={currentMap.image_path}
                                            alt={currentMap.name}
                                            style={{
                                                width: '100%',
                                                height: 'auto',
                                                opacity: 0,
                                                pointerEvents: 'none',
                                                position: 'absolute'
                                            }}
                                            onLoad={handleImageLoad}
                                            onError={(e) => {
                                                e.target.src = '/images/loading.gif';
                                            }}
                                        />
                                        {mapPoints.map((point) => {
                                            const isLocked = point.is_unlocked === false;
                                            return (
                                                <div
                                                    key={point.id}
                                                    onClick={() => handlePointClick(point)}
                                                    onMouseEnter={() => handleMouseEnter(point)}
                                                    onMouseLeave={handleMouseLeave}
                                                    style={{
                                                        ...getPointStyle(point),
                                                        opacity: isLocked ? 0.5 : 1,
                                                        cursor: isLocked ? 'not-allowed' : 'pointer',
                                                        filter: isLocked ? 'grayscale(50%)' : 'none'
                                                    }}
                                                    title={isLocked ? 
                                                        (point.unlock_requirement ? 
                                                            `נעול: ${point.unlock_requirement.current_attacks}/${point.unlock_requirement.required_attacks} התקפות על ${point.unlock_requirement.required_on_point_name}` : 
                                                            'נעול') : 
                                                        (point.point_type === 'trainer' && point.trainer_id ? 
                                                            `${point.tooltip || point.name} - מאמן (${point.silver_cost || '?'} סילבר)` : 
                                                            (point.tooltip || point.name))}
                                                >
                                                    {hoveredArea?.id === point.id && (
                                                        <span style={{ 
                                                            color: 'white', 
                                                            fontWeight: 'bold', 
                                                            fontSize: '12px',
                                                            textShadow: '1px 1px 2px black',
                                                            pointerEvents: 'none'
                                                        }}>
                                                            {point.name} {point.point_type === 'trainer' && '👤'} {isLocked && '(נעול)'}
                                                        </span>
                                                    )}
                                                    {isLocked && (
                                                        <span style={{
                                                            position: 'absolute',
                                                            top: '50%',
                                                            left: '50%',
                                                            transform: 'translate(-50%, -50%)',
                                                            color: 'red',
                                                            fontWeight: 'bold',
                                                            fontSize: '16px',
                                                            textShadow: '2px 2px 4px black',
                                                            pointerEvents: 'none'
                                                        }}>
                                                            🔒
                                                        </span>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                            
                            {/* Map Points List */}
                            {mapPoints.length > 0 && (
                                <Card border='dark' text='white' className='bg-theme mt-2'>
                                    <Card.Header>
                                        <h5>{t('Available Areas')}</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <Row>
                                            {mapPoints.map((point) => {
                                                const isLocked = point.is_unlocked === false;
                                                return (
                                                    <Col md={4} key={point.id} className="mb-2">
                                                        <Button
                                                            variant={isLocked ? "secondary" : "outline-light"}
                                                            className="w-100"
                                                            onClick={() => handlePointClick(point)}
                                                            onMouseEnter={() => handleMouseEnter(point)}
                                                            onMouseLeave={handleMouseLeave}
                                                            disabled={isLocked}
                                                            style={{
                                                                opacity: isLocked ? 0.6 : 1
                                                            }}
                                                        >
                                                            {point.name} {point.point_type === 'trainer' && '👤'} ({point.point_type === 'trainer' ? `מאמן - ${point.silver_cost || '?'} סילבר` : point.gebied})
                                                            {isLocked && ' 🔒'}
                                                            {point.attack_count > 0 && ` [${point.attack_count} תקיפות]`}
                                                        </Button>
                                                    </Col>
                                                );
                                            })}
                                        </Row>
                        </Card.Body>
                    </Card>
                            )}
                        </>
                    )}
                </div>
                </div>
            </GoldSiverHeader>
            
            {/* Learn New Attack Modal - shows after returning from battle */}
            <LearnAttackModal
                show={showLearnAttackModal}
                onHide={() => {
                    setShowLearnAttackModal(false);
                    setPendingAttackData(null);
                    localStorage.removeItem('pendingAttackData');
                }}
                pendingAttackData={pendingAttackData}
                onAttackLearned={() => {
                    // Refresh Pokemon data
                    dispatch(getPlayerPokemons());
                    setShowLearnAttackModal(false);
                    setPendingAttackData(null);
                    localStorage.removeItem('pendingAttackData');
                }}
            />
        </div>
    )
}

export default Map
